import React, { useCallback, useMemo } from "react";
import { includes, map } from "lodash";
import { Facet } from "@linkfluence/models/facet";
import { Metric } from "@linkfluence/models/metric";
import Subtitle from "@radarly/components/cards/card/subtitle";
import { useCustomFields } from "@radarly/core/custom-field/use-cases";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { DateTermCard } from "@radarly/models/cards";
import { getValidDateTermFragment } from "@radarly/models/cards/fragment";
import { isTimeUnitCard } from "@radarly/models/cards/is";
import { getDateFromClause } from "@radarly/models/clause/getter";
import { getPostSortFromMetric } from "@radarly/models/posts";
import { getTermFromAggs } from "@radarly/models/search";
import Date from "@radarly/shared/cards/subtitle/date";
import SubtitleMetric from "@radarly/shared/cards/subtitle/metric";
import Tile from "@radarly/shared/cards/tile";
import Widget from "@radarly/shared/widgets/date-term";
import { getFocusClause } from "../clause";
import { useFacets } from "../facets";
import { useFragments } from "../fragment";
import { useGenerateCSV } from "../hooks/useGenerateCSV";
import { CardProps } from "../model";

const RadarlyDateTermCards = ({
  card,
  openPostsPanel,
  height,
  timezone,
  forceReload,
  nowDate,
  ...props
}: CardProps<DateTermCard>) => {
  const project = useCurrentProject();
  const timeUnit = isTimeUnitCard(card) ? card.dataviz.timeUnit : undefined;
  const fragments = useFragments(
    [card.fragments[0], card.fragments[1]],
    props.clause,
    props.from,
    props.to,
    timezone,
    timeUnit
  );
  const dataviz = card.dataviz;
  const fragment = fragments[0];
  const term = getTermFromAggs(fragment.request.aggs.date.aggs) as Facet;
  const cleanFragment = getValidDateTermFragment(card, term, fragment);
  const cleanClause = useMemo(
    () => (cleanFragment ? cleanFragment.request.query : { and: [] }),
    [cleanFragment]
  );
  const fields = useCustomFields();
  const facet = useFacets(term, cleanClause);
  const title = card.title;
  const { onGenerateCSV } = useGenerateCSV(title, fragments[0], fragments[1]);

  const onClickItem = useCallback(
    (
      id: string | number | Array<string | number>,
      range?: { from: Date; to: Date }
    ) => {
      const { from, to } = getDateFromClause(cleanClause);
      const formattedId =
        facet.id === Facet.ReviewRate ? id.toString().replace(" ★", "") : id;
      openPostsPanel({
        clause: cleanClause,
        period: {
          from: range ? range.from : from,
          to: range ? range.to : to,
        },
        title,
        focusClause: getFocusClause(
          term,
          formattedId,
          includes(map(fields, "label"), term)
        ),
        sortBy: getPostSortFromMetric(
          cleanFragment
            ? cleanFragment.request.aggs.date.aggs[term].metrics[0]
            : Metric.Impression
        ),
        timezone,
      });
    },
    [
      cleanClause,
      facet.id,
      openPostsPanel,
      title,
      term,
      fields,
      cleanFragment,
      timezone,
    ]
  );
  return (
    <Tile
      title={title}
      height={height}
      subtitle={
        <Subtitle
          meta={[
            <Date fragment={fragment} timezone={timezone} nowDate={nowDate} />,
            <SubtitleMetric
              metric={
                cleanFragment
                  ? cleanFragment.request.aggs.date.metrics[0]
                  : Metric.Impression
              }
            />,
          ]}
        />
      }
      clause={cleanClause}
      onGenerateCSV={onGenerateCSV}
      isPNG
    >
      <Widget
        fragment={cleanFragment}
        project={project.id}
        dataviz={dataviz}
        facet={facet}
        onClickLegend={openPostsPanel ? onClickItem : null}
        onClickElement={openPostsPanel ? onClickItem : null}
        forceReload={forceReload}
        nowDate={nowDate}
      />
    </Tile>
  );
};

export default RadarlyDateTermCards;
